import { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import { CAD_PROJECT_PIN_STYLE, buildCadProjectPinCircleRadiusPaint } from '../../Map/styles/mapTheme';
import { whenMapStyleReady } from '../../Map/hooks/useListPinsLayer';
import {
  buildCadProjectPinTooltipHtml,
  cadProjectPinImageId,
  cadProjectPinPaintCoordinates,
  cadProjectPinsToGeoJSON,
  readCadProjectPins,
} from '../utils/cadProjectPins';
import {
  ensureCadProjectPinImageForId,
  ensureCadProjectPinImages,
} from '../utils/cadProjectPinIcon';
import { CAD_PROJECT_PIN_KIND_CUSTOM } from '../data/cadProjectPinKinds';
import { scheduleCadSymbolOp } from '../utils/afterCadSymbolPlacement';
import {
  CAD_PROJECT_PINS_ICON_LAYER_ID,
  CAD_PROJECT_PINS_LAYER_ID,
  CAD_PROJECT_PINS_SOURCE_ID,
  moveCadProjectPinsForeground,
  queryCadProjectPinAtPoint,
  removeCadProjectPinLayers,
} from '../utils/cadProjectPinLayers';

export {
  CAD_PROJECT_PINS_LAYER_ID,
  CAD_PROJECT_PINS_ICON_LAYER_ID,
  CAD_PROJECT_PINS_SOURCE_ID,
  moveCadProjectPinsForeground,
  queryCadProjectPinAtPoint,
};

/**
 * Pines custom de una lista CAD. Círculo de color (fiable) + badge symbol
 * encima si las imágenes cargan. No es `reference_places`.
 */
export function useCadProjectPinsLayer({
  mapInstanceRef,
  project,
  featureCollection = null,
  paintMode = 'map',
  enabled = true,
  visible = true,
  interactive = true,
  onSelectPin = null,
  highlightedPinId = null,
  /** `share` = chrome de lotes en `/#/lista/:id`; default POI. */
  tooltipVariant = 'place',
}) {
  const mapRef = useRef(null);
  const onSelectPinRef = useRef(onSelectPin);
  onSelectPinRef.current = onSelectPin;
  const projectRef = useRef(project);
  projectRef.current = project;
  const featureCollectionRef = useRef(featureCollection);
  featureCollectionRef.current = featureCollection;
  const paintModeRef = useRef(paintMode);
  paintModeRef.current = paintMode;
  const visibleRef = useRef(visible);
  visibleRef.current = visible;
  const interactiveRef = useRef(interactive);
  interactiveRef.current = interactive;
  const tooltipVariantRef = useRef(tooltipVariant);
  tooltipVariantRef.current = tooltipVariant;
  const prevHighlightIdRef = useRef(null);
  const popupRef = useRef(null);
  const [mapAttachVersion, setMapAttachVersion] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    const map = mapInstanceRef.current;
    if (map) {
      setMapAttachVersion((version) => version + 1);
      return undefined;
    }
    let attempts = 0;
    const intervalId = window.setInterval(() => {
      attempts += 1;
      if (mapInstanceRef.current) {
        setMapAttachVersion((version) => version + 1);
        window.clearInterval(intervalId);
        return;
      }
      if (attempts >= 120) window.clearInterval(intervalId);
    }, 50);
    return () => window.clearInterval(intervalId);
  }, [enabled, mapInstanceRef]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    mapRef.current = map;
    if (!map || !enabled) {
      removeCadProjectPinLayers(map);
      return undefined;
    }

    const shareTooltip = tooltipVariant === 'share';
    const popup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 14,
      className: shareTooltip
        ? 'terrio-parcel-tooltip terrio-cad-share-tooltip'
        : 'terrio-place-tooltip',
      maxWidth: shareTooltip ? '240px' : '259px',
    });
    popupRef.current = popup;
    const canvas = map.getCanvas();
    let hovering = false;
    let paintQueued = false;

    const hidePopup = () => {
      popup.remove();
      if (hovering) {
        canvas.style.cursor = '';
        hovering = false;
      }
    };

    const currentGeoJSON = () => cadProjectPinsToGeoJSON(
      readCadProjectPins(projectRef.current),
      paintModeRef.current,
      projectRef.current,
      featureCollectionRef.current,
    );

    const paint = () => {
      paintQueued = false;
      upsertCadProjectPinLayers(map, currentGeoJSON(), visibleRef.current);
    };

    const pinTooltipHtml = (props) => buildCadProjectPinTooltipHtml(
      props,
      { variant: tooltipVariantRef.current },
    );

    const handleMove = (event) => {
      if (!visibleRef.current || !interactiveRef.current) {
        hidePopup();
        return;
      }
      const feature = event.features?.[0];
      if (!feature) {
        hidePopup();
        return;
      }
      const html = pinTooltipHtml(feature.properties || {});
      hovering = true;
      canvas.style.cursor = 'pointer';
      if (html) popup.setLngLat(event.lngLat).setHTML(html).addTo(map);
    };

    const handleClick = (event) => {
      if (!visibleRef.current || !interactiveRef.current) return;
      event.preventDefault();
      if (event.originalEvent) event.originalEvent.__cadProjectPinClick = true;
      const props = event.features?.[0]?.properties || {};
      const id = String(props.id || '').trim();
      if (!id) return;
      onSelectPinRef.current?.({
        id,
        name: props.name || '',
        notes: props.notes || null,
        kind: props.kind || CAD_PROJECT_PIN_KIND_CUSTOM,
      });
    };

    const handleMissingImage = (event) => {
      ensureCadProjectPinImageForId(map, event?.id);
    };

    const handleStyleData = () => {
      try {
        if (map.getSource(CAD_PROJECT_PINS_SOURCE_ID) && map.getLayer(CAD_PROJECT_PINS_LAYER_ID)) {
          ensureCadProjectPinImages(map);
          ensureCadProjectPinIconLayer(map, visibleRef.current);
          moveCadProjectPinsForeground(map);
          return;
        }
      } catch (_) { /* estilo a medias */ }
      if (paintQueued) return;
      paintQueued = true;
      window.requestAnimationFrame(paint);
    };

    const cancelWait = whenMapStyleReady(map, paint);
    map.on('styledata', handleStyleData);
    map.on('styleimagemissing', handleMissingImage);
    map.on('mouseenter', CAD_PROJECT_PINS_LAYER_ID, handleMove);
    map.on('mousemove', CAD_PROJECT_PINS_LAYER_ID, handleMove);
    map.on('mouseleave', CAD_PROJECT_PINS_LAYER_ID, hidePopup);
    map.on('click', CAD_PROJECT_PINS_LAYER_ID, handleClick);
    map.on('mouseenter', CAD_PROJECT_PINS_ICON_LAYER_ID, handleMove);
    map.on('mousemove', CAD_PROJECT_PINS_ICON_LAYER_ID, handleMove);
    map.on('mouseleave', CAD_PROJECT_PINS_ICON_LAYER_ID, hidePopup);
    map.on('click', CAD_PROJECT_PINS_ICON_LAYER_ID, handleClick);
    canvas.addEventListener('mouseleave', hidePopup);

    return () => {
      cancelWait();
      hidePopup();
      popupRef.current = null;
      try { map.off('styledata', handleStyleData); } catch (_) { /* noop */ }
      try { map.off('styleimagemissing', handleMissingImage); } catch (_) { /* noop */ }
      try { map.off('mouseenter', CAD_PROJECT_PINS_LAYER_ID, handleMove); } catch (_) { /* noop */ }
      try { map.off('mousemove', CAD_PROJECT_PINS_LAYER_ID, handleMove); } catch (_) { /* noop */ }
      try { map.off('mouseleave', CAD_PROJECT_PINS_LAYER_ID, hidePopup); } catch (_) { /* noop */ }
      try { map.off('click', CAD_PROJECT_PINS_LAYER_ID, handleClick); } catch (_) { /* noop */ }
      try { map.off('mouseenter', CAD_PROJECT_PINS_ICON_LAYER_ID, handleMove); } catch (_) { /* noop */ }
      try { map.off('mousemove', CAD_PROJECT_PINS_ICON_LAYER_ID, handleMove); } catch (_) { /* noop */ }
      try { map.off('mouseleave', CAD_PROJECT_PINS_ICON_LAYER_ID, hidePopup); } catch (_) { /* noop */ }
      try { map.off('click', CAD_PROJECT_PINS_ICON_LAYER_ID, handleClick); } catch (_) { /* noop */ }
      canvas.removeEventListener('mouseleave', hidePopup);
    };
  }, [enabled, mapInstanceRef, mapAttachVersion, tooltipVariant]);

  useEffect(() => {
    if (!enabled) return;
    const map = mapInstanceRef.current;
    if (!map) return;
    if (!visible) popupRef.current?.remove();
    upsertCadProjectPinLayers(
      map,
      cadProjectPinsToGeoJSON(
        readCadProjectPins(project),
        paintMode,
        project,
        featureCollection,
      ),
      visible,
    );
  }, [
    enabled,
    project,
    featureCollection,
    paintMode,
    visible,
    mapInstanceRef,
    mapAttachVersion,
  ]);

  useEffect(() => {
    if (!enabled) return;
    const map = mapInstanceRef.current;
    if (!map || !map.getSource(CAD_PROJECT_PINS_SOURCE_ID)) return;

    const prevId = prevHighlightIdRef.current;
    const nextId = String(highlightedPinId || '').trim() || null;
    if (prevId && prevId !== nextId) {
      try {
        map.setFeatureState(
          { source: CAD_PROJECT_PINS_SOURCE_ID, id: prevId },
          { highlight: false },
        );
      } catch (_) { /* noop */ }
    }
    prevHighlightIdRef.current = nextId;

    const popup = popupRef.current;
    if (!nextId) {
      popup?.remove();
      return;
    }
    try {
      map.setFeatureState(
        { source: CAD_PROJECT_PINS_SOURCE_ID, id: nextId },
        { highlight: true },
      );
    } catch (_) { /* promoteId aún no */ }

    if (!popup) return;
    const pin = readCadProjectPins(project).find((row) => row.id === nextId);
    const coords = pin
      ? cadProjectPinPaintCoordinates(pin, paintMode, project, featureCollection)
      : null;
    if (!coords) {
      popup.remove();
      return;
    }
    const html = buildCadProjectPinTooltipHtml({
      name: pin.name,
      notes: pin.notes,
      kind: pin.kind,
    }, { variant: tooltipVariant });
    if (!html) {
      popup.remove();
      return;
    }
    popup.setLngLat(coords).setHTML(html).addTo(map);
  }, [
    enabled,
    highlightedPinId,
    project,
    featureCollection,
    paintMode,
    tooltipVariant,
    mapInstanceRef,
    mapAttachVersion,
  ]);

  useEffect(() => {
    if (!enabled) removeCadProjectPinLayers(mapRef.current);
  }, [enabled]);

  useEffect(() => () => {
    removeCadProjectPinLayers(mapRef.current);
  }, []);
}

const CIRCLE_RADIUS_PAINT = buildCadProjectPinCircleRadiusPaint();

/**
 * @param {object} map
 * @param {object} geojson
 * @param {boolean} visible
 */
function upsertCadProjectPinLayers(map, geojson, visible) {
  try {
    const source = map.getSource(CAD_PROJECT_PINS_SOURCE_ID);
    const circleLayer = map.getLayer(CAD_PROJECT_PINS_LAYER_ID);
    // Solo recrear si quedó un symbol viejo (HMR). No exigir `.type`:
    // en algunos builds getLayer no lo expone y el teardown ciclaba.
    const isLegacySymbol = Boolean(circleLayer) && circleLayer.type === 'symbol';
    const hasCircle = Boolean(circleLayer) && !isLegacySymbol;
    if (source && hasCircle) {
      source.setData(geojson);
      applyCadProjectPinLayerVisibility(map, visible);
      applyCadProjectPinCirclePaint(map);
      ensureCadProjectPinIconLayer(map, visible);
      moveCadProjectPinsForeground(map);
      return;
    }
    if (source || circleLayer || map.getLayer(CAD_PROJECT_PINS_ICON_LAYER_ID)) {
      removeCadProjectPinLayers(map);
    }
    map.addSource(CAD_PROJECT_PINS_SOURCE_ID, {
      type: 'geojson',
      data: geojson,
      promoteId: 'id',
    });
    map.addLayer({
      id: CAD_PROJECT_PINS_LAYER_ID,
      type: 'circle',
      source: CAD_PROJECT_PINS_SOURCE_ID,
      minzoom: CAD_PROJECT_PIN_STYLE.minZoom,
      layout: {
        visibility: visible ? 'visible' : 'none',
      },
      paint: {
        'circle-color': [
          'coalesce',
          ['get', 'color'],
          CAD_PROJECT_PIN_STYLE.strokeColor,
        ],
        'circle-radius': CIRCLE_RADIUS_PAINT,
        'circle-stroke-width': CAD_PROJECT_PIN_STYLE.circleStrokeWidth,
        'circle-stroke-color': CAD_PROJECT_PIN_STYLE.strokeColor,
        'circle-opacity': CAD_PROJECT_PIN_STYLE.circleOpacity,
        'circle-stroke-opacity': CAD_PROJECT_PIN_STYLE.circleOpacity,
      },
    });
    ensureCadProjectPinIconLayer(map, visible);
    moveCadProjectPinsForeground(map);
  } catch (error) {
    console.error('CAD pins: no se pudo pintar la capa', error);
  }
}

/**
 * @param {object} map
 * @param {boolean} visible
 */
function applyCadProjectPinLayerVisibility(map, visible) {
  const visibility = visible ? 'visible' : 'none';
  try {
    map.setLayoutProperty(CAD_PROJECT_PINS_LAYER_ID, 'visibility', visibility);
  } catch (_) { /* noop */ }
  if (map.getLayer(CAD_PROJECT_PINS_ICON_LAYER_ID)) {
    try {
      map.setLayoutProperty(CAD_PROJECT_PINS_ICON_LAYER_ID, 'visibility', visibility);
    } catch (_) { /* noop */ }
  }
}

/**
 * @param {object} map
 */
function applyCadProjectPinCirclePaint(map) {
  try {
    map.setPaintProperty(CAD_PROJECT_PINS_LAYER_ID, 'circle-radius', CIRCLE_RADIUS_PAINT);
    map.setPaintProperty(
      CAD_PROJECT_PINS_LAYER_ID,
      'circle-stroke-width',
      CAD_PROJECT_PIN_STYLE.circleStrokeWidth,
    );
    map.setPaintProperty(
      CAD_PROJECT_PINS_LAYER_ID,
      'circle-opacity',
      CAD_PROJECT_PIN_STYLE.circleOpacity,
    );
    map.setPaintProperty(
      CAD_PROJECT_PINS_LAYER_ID,
      'circle-stroke-opacity',
      CAD_PROJECT_PIN_STYLE.circleOpacity,
    );
    map.setLayerZoomRange(CAD_PROJECT_PINS_LAYER_ID, CAD_PROJECT_PIN_STYLE.minZoom, 24);
  } catch (_) { /* estilo a medias */ }
}

/**
 * Badge con glifo. Se agrega diferido (symbol placement) y no bloquea el círculo.
 *
 * @param {object} map
 * @param {boolean} visible
 */
function ensureCadProjectPinIconLayer(map, visible) {
  if (!map || map.getLayer(CAD_PROJECT_PINS_ICON_LAYER_ID)) {
    if (map?.getLayer(CAD_PROJECT_PINS_ICON_LAYER_ID)) {
      try {
        map.setLayoutProperty(CAD_PROJECT_PINS_ICON_LAYER_ID, 'icon-size', CAD_PROJECT_PIN_STYLE.iconSize);
        map.setPaintProperty(
          CAD_PROJECT_PINS_ICON_LAYER_ID,
          'icon-opacity',
          CAD_PROJECT_PIN_STYLE.circleOpacity,
        );
        map.setLayerZoomRange(
          CAD_PROJECT_PINS_ICON_LAYER_ID,
          CAD_PROJECT_PIN_STYLE.minZoom,
          24,
        );
      } catch (_) { /* noop */ }
    }
    return;
  }
  if (!ensureCadProjectPinImages(map)) return;
  scheduleCadSymbolOp(CAD_PROJECT_PINS_ICON_LAYER_ID, () => {
    try {
      if (!map.getSource(CAD_PROJECT_PINS_SOURCE_ID)) return;
      if (map.getLayer(CAD_PROJECT_PINS_ICON_LAYER_ID)) return;
      if (!ensureCadProjectPinImages(map)) return;
      map.addLayer({
        id: CAD_PROJECT_PINS_ICON_LAYER_ID,
        type: 'symbol',
        source: CAD_PROJECT_PINS_SOURCE_ID,
        minzoom: CAD_PROJECT_PIN_STYLE.minZoom,
        layout: {
          'icon-image': [
            'coalesce',
            ['get', 'icon'],
            cadProjectPinImageId(CAD_PROJECT_PIN_KIND_CUSTOM),
          ],
          'icon-size': CAD_PROJECT_PIN_STYLE.iconSize,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-anchor': 'center',
          'icon-padding': 0,
          visibility: visible ? 'visible' : 'none',
        },
        paint: {
          'icon-opacity': CAD_PROJECT_PIN_STYLE.circleOpacity,
        },
      });
      moveCadProjectPinsForeground(map);
    } catch (error) {
      console.error('CAD pins: no se pudo pintar el badge', error);
    }
  });
}

/**
 * @param {object} event
 * @returns {boolean}
 */
export function isCadProjectPinEvent(event) {
  if (event?.originalEvent?.__cadProjectPinClick) return true;
  const map = event?.target;
  const point = event?.point;
  if (!map || !point || typeof map.getLayer !== 'function') return false;
  return Boolean(queryCadProjectPinAtPoint(map, point));
}
